import { useMemo } from 'react'

import { DashCard } from './DashboardCards'
import { resolutionMix } from '../../../lib/dashboard-insights'

import type { GisIssue } from '../../../api/types'
import type { ResolutionTargets } from '../../../lib/resolution-targets'

/** Segment meta — the same green/amber/red the app's status language already uses. */
const SEGMENTS = [
  { key: 'overdue', label: 'Past target', color: '#dc2626' },
  { key: 'dueSoon', label: 'Due soon (≤3d)', color: '#f59e0b' },
  { key: 'onTrack', label: 'On track', color: '#10b981' },
] as const

interface ResolutionHealthCardProps {
  /** Open issues in the current scope. */
  openIssues: GisIssue[]
  targets: ResolutionTargets
  pending: boolean
  /** Every drill lands on the risk-sorted Issues board — breaching issues lead it by definition. */
  onOpen: () => void
}

/**
 * "Resolution health" — how the open load sits against the per-severity resolution targets:
 * a 100% stacked bar (past target / due soon / on track) with the counts spelled out, and the
 * targets themselves in the footer so the buckets are never a mystery. Clicking anywhere opens
 * the Issues board, whose risk sort leads with exactly these breaching issues.
 */
export function ResolutionHealthCard({ openIssues, targets, pending, onOpen }: ResolutionHealthCardProps) {
  const mix = useMemo(() => resolutionMix(openIssues, targets), [openIssues, targets])
  const counts: Record<(typeof SEGMENTS)[number]['key'], number> = {
    overdue: mix.overdue,
    dueSoon: mix.dueSoon,
    onTrack: mix.onTrack,
  }
  const share = (n: number): number => (mix.total > 0 ? Math.round((n / mix.total) * 100) : 0)

  return (
    <DashCard title="Resolution health" caption="Open issues vs targets" onOpen={onOpen} openLabel="Open Issues board (risk-sorted)">
      {pending ? (
        <div className="flex flex-col gap-2 py-1" aria-hidden>
          <div className="h-[14px] animate-pulse rounded-full bg-gis-track-2" />
          <div className="h-[54px] animate-pulse rounded-[8px] bg-gis-track-2" />
        </div>
      ) : mix.total === 0 ? (
        <div className="py-4 text-center text-[11.5px] text-gis-soft">No open issues in this selection.</div>
      ) : (
        <>
          {/* 100% stacked bar — 2px gaps so the three shares stay separable at a glance. */}
          <div className="flex h-[14px] gap-[2px] overflow-hidden rounded-full" role="img"
            aria-label={`${mix.overdue} past target, ${mix.dueSoon} due soon, ${mix.onTrack} on track`}>
            {SEGMENTS.map((s) =>
              counts[s.key] > 0 ? (
                <span
                  key={s.key}
                  className="block h-full min-w-[4px] transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                  style={{ width: `${(counts[s.key] / mix.total) * 100}%`, background: s.color }}
                />
              ) : null,
            )}
          </div>
          <div className="mt-2 flex flex-col">
            {SEGMENTS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={onOpen}
                title="Open the Issues board — breaching issues lead the risk sort"
                className="-mx-1 flex cursor-pointer items-center justify-between gap-2 rounded-[8px] px-1 py-[4px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
              >
                <span className="flex min-w-0 items-center gap-2 text-[11.5px] font-semibold text-gis-ink-2">
                  <span className="h-[9px] w-[9px] flex-none rounded-[3px]" style={{ background: s.color }} />
                  {s.label}
                </span>
                <span className="flex flex-none items-center gap-[6px]">
                  <span className="min-w-[18px] text-right text-[11.5px] font-bold tabular-nums text-gis-ink">
                    {counts[s.key]}
                  </span>
                  <span className="min-w-[32px] text-right text-[10px] tabular-nums text-gis-muted">
                    ({share(counts[s.key])}%)
                  </span>
                </span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-[9.5px] leading-snug text-gis-muted">
            Targets: critical {targets.critical}d · non-critical {targets.non_critical}d
          </p>
        </>
      )}
    </DashCard>
  )
}
